import { Icon } from "@circle-react/components/shared/Icon";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { useWavesurfer } from "@wavesurfer/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import RecordPlugin from "wavesurfer.js/dist/plugins/record.esm.js";

import { t } from "@/i18n-js/instance";
import { legacyClassNames } from "@/react/helpers/cssAnchorHelpers";

import { MAX_RECORDING_DURATION_MINUTES } from "../constants";
import { formatTime, generateRandomString } from "../helpers";
import { useWaveSurferRecorder } from "../hooks/useWaveSurferRecorder";

import "../WaveSurferAudioPlayer/audio-player.scss";
import { useWebsocket } from "../hooks/useWebsocket";
import { Spinner } from "./Spinner";

interface WaveSurferRecorderProps {
  onClose: () => void;
}

export const WaveSurferRecorder = ({ onClose }: WaveSurferRecorderProps) => {
  const recordingId = useMemo(() => generateRandomString(), []);
  const waveformRef = useRef<HTMLDivElement>(null);
  const recordRef = useRef<ReturnType<typeof RecordPlugin.create> | null>(null);
  const cancelledRef = useRef(false);
  const [duration, setDuration] = useState(0);

  const { isBusy, isError, handleBlob, onRetry, setServerError } =
    useWaveSurferRecorder({
      recordingId,
    });
  useWebsocket({ onClose, recordingId, setServerError });

  const wsOptions = useMemo(
    () => ({
      container: waveformRef,
      waveColor: "#C8CCD2",
      progressColor: "#191B1F",
      cursorColor: "transparent",
      barWidth: 3,
      barGap: 3,
      barRadius: 4,
      height: 32,
      normalize: true,
    }),
    [],
  );

  const { wavesurfer } = useWavesurfer(wsOptions);

  useEffect(() => {
    if (!wavesurfer) return;

    const record = wavesurfer.registerPlugin(
      RecordPlugin.create({
        scrollingWaveform: true,
        scrollingWaveformWindow: 7,
        renderRecordedAudio: false,
      }),
    );

    recordRef.current = record;

    const unsubProgress = record.on("record-progress", (timeMs: number) => {
      setDuration(Math.floor(timeMs / 1000));
    });

    const unsubEnd = record.on("record-end", (blob: Blob) => {
      if (!cancelledRef.current) {
        handleBlob(blob);
      }
    });

    void record.startRecording();

    const timeout = setTimeout(
      () => {
        if (record.isRecording()) {
          record.stopRecording();
        }
      },
      MAX_RECORDING_DURATION_MINUTES * 60 * 1000,
    );

    return () => {
      clearTimeout(timeout);
      cancelledRef.current = true;
      unsubProgress();
      unsubEnd();
      if (record.isRecording()) {
        record.stopRecording();
      }
      record.destroy();
    };
  }, [wavesurfer, handleBlob]);

  const handleClose = useCallback(() => {
    cancelledRef.current = true;
    if (recordRef.current?.isRecording()) {
      recordRef.current.stopRecording();
    }
    onClose();
  }, [onClose]);

  const handleSubmit = useCallback(() => {
    if (isError) {
      onRetry();
    } else if (recordRef.current?.isRecording()) {
      recordRef.current.stopRecording();
    }
  }, [isError, onRetry]);

  return (
    <div className="bg-primary shadow-voice-message absolute left-0 top-0 z-10 flex size-full items-center justify-center rounded-md">
      <div className="flex items-center gap-3 rounded-xl border border-primary bg-primary p-2 w-[317px]">
        <button
          type="button"
          onClick={handleClose}
          className="bg-primary flex size-8 shrink-0 items-center justify-center rounded-md border border-primary"
          aria-label={t("tiptap.audio_player.cancel")}
        >
          <Icon type="20-close" viewBox="0 0 20 20" className="text-dark" />
        </button>

        <div
          ref={waveformRef}
          className={classNames(
            legacyClassNames("audio-player-wavesurfer__waveform"),
            "min-w-[120px] flex-1",
          )}
        />

        <span className="shrink-0 whitespace-nowrap text-sm tabular-nums text-dark">
          {formatTime(duration)}
        </span>

        <button
          type="button"
          className="bg-inverse flex size-8 shrink-0 items-center justify-center rounded-md"
          onClick={handleSubmit}
          disabled={isBusy}
          aria-label={t("tiptap.audio_player.send")}
        >
          {isBusy && <Spinner size={16} />}
          {!isBusy && isError && (
            <Icon
              type="20-retry"
              viewBox="0 0 20 20"
              className="text-inverse"
            />
          )}
          {!isBusy && !isError && (
            <Icon type="20-arrow-up" size={20} className="text-inverse" />
          )}
        </button>
      </div>
    </div>
  );
};
